import { useMemo } from 'react';
import styled from 'styled-components/macro';
import { Box, Flex, Tooltip } from 'components/ui';
import { MixSpecs } from 'utils/types';
import { allEffects, getColorVariant } from 'utils/futura';
import playIconSmall from 'img/play-icon-small.svg';
import playIconSmallBlack from 'img/play-icon-small-black.svg';
import stopIconSmall from 'img/stop-icon-small.svg';
import stopIconSmallBlack from 'img/stop-icon-small-black.svg';
import Color from 'color';
import muteIcon from 'img/mute-icon.svg';
import { FUTURA_PALETTES } from '../../constants';

interface ClipSelectProps {
  mixASpecs: MixSpecs;
  mixBSpecs: MixSpecs;
  layer: string;
  section: string;
  value: string;
  onChange: (any) => void;
  onPlayClip: (slipSelection: string) => void;
  notesPlaying: any;
}

const ClipWrapper = styled(Flex)<any>`
  cursor: pointer;
  height: 32px;
  padding: 4px;
  border-radius: 3px;
  width: 100%;
  align-mixs: center;

  .play {
    display: none;
    margin: 0 4px;
  }

  &:hover {
    background-color: ${props => props.bgColor};
    color: ${props => props.colorHover};

    .play {
      display: block;

      &:hover {
        transform: scale(1.5);
      }
    }
  }
`;

const getTraits = variant => {
  const colorPalette = variant?.traits.find(trait => trait.name === 'ColorPalette')?.value;
  const noteShape = variant?.traits.find(trait => trait.name === 'NoteShape')?.value;
  let effects = variant?.traits.filter(trait => trait.name === 'Effect').map(e => e.value);

  return {
    noteShape: ['Square', 'Line', 'Triangle'][parseInt(noteShape || '0')],
    colorPalette: FUTURA_PALETTES[parseInt(colorPalette || '0')].name,
    effects: allEffects.filter(e => effects.indexOf(e.name) !== -1),
  };
};

const getNoteColor = (mixSpecs, pieceSection, pieceLayer, pickedVariantId): string => {
  const pickedVariantIndex = pieceSection.variants.findIndex(mix => mix.id === pickedVariantId);
  const color = mixSpecs.palette.layers[pieceLayer.index % mixSpecs.palette.layers.length];
  const noteColor = getColorVariant(color, pickedVariantIndex) || mixSpecs.palette.layers[0];

  return noteColor;
};

const EBF = window.EulerBeatsFutura;

const Clip = ({ onClick, selected, mixSpecs, section, layer, onPlayClip, notesPlaying }) => {
  const clipName = mixSpecs.attributes[`${section} ${layer}`];
  const isNone = clipName === 'None';

  const { pieceSpec: spec } = mixSpecs.pieceData.options;
  const { piece: specReader } = mixSpecs.pieceData;
  const pieceLayer = spec.layers.find(l => l.name === layer);
  const pieceSection = pieceLayer.sections.find(s => s.section.name === section);
  const pickedVariantId = specReader.mixCode.pickValues[pieceLayer.index][pieceSection.index];
  const variant = specReader.getVariant(pickedVariantId);

  const isPlaying =
    !isNone &&
    notesPlaying.hasNotePlaying(pieceSection.index, null, variant.index, pieceLayer.index);

  const noteColor = useMemo(
    () => getNoteColor(mixSpecs, pieceSection, pieceLayer, pickedVariantId),
    [mixSpecs, pieceSection, pieceLayer, pickedVariantId]
  );
  const textColor = Color(noteColor).isLight() ? 'black' : 'white';
  const playIcon = Color(noteColor).isLight() ? playIconSmallBlack : playIconSmall;
  const stopIcon = Color(noteColor).isLight() ? stopIconSmallBlack : stopIconSmall;
  const { colorPalette, noteShape, effects } = getTraits(variant);

  const playClip = e => {
    e.stopPropagation();
    if (isPlaying) {
      EBF.visual.stop();
    } else {
      onPlayClip(clipName);
    }
  };

  return (
    <Flex
      onClick={onClick}
      backgroundColor={selected ? 'black' : 'transparent'}
      border={`1px solid ${selected ? '#333' : 'transparent'}`}
      borderRadius="3px"
    >
      <Box
        width="8px"
        height="32px"
        borderRadius="3px"
        backgroundColor={selected ? noteColor : 'transparent'}
        mr={1}
      >
        &nbsp;
      </Box>
      {!isNone && (
        <Tooltip id={`tooltip_${clipName}`} place="top">
          <Box>Color Palette: {colorPalette}</Box>
          <Box>Note Shape: {noteShape}</Box>
          {effects && effects.length > 0 && (
            <Flex mt={2}>
              {effects.map(effect => (
                <Box key={effect.name}>
                  <img src={effect.icon} alt={effect.name} width="24px" />
                </Box>
              ))}
            </Flex>
          )}
        </Tooltip>
      )}
      <ClipWrapper
        bgColor={noteColor}
        colorHover={textColor}
        data-for={`tooltip_${clipName}`}
        border={`1px solid ${isPlaying ? noteColor : 'transparent'}`}
        data-tip={`tip_${clipName}`}
        alignCenter
      >
        {isNone ? (
          <>
            <img src={muteIcon} alt="muted" width="14px" />
            <Box ml={2}>muted</Box>
          </>
        ) : (
          <>
            <div className="play" onClick={playClip}>
              <img src={isPlaying ? stopIcon : playIcon} alt="play" width="8px" />
            </div>
            <div>{clipName}</div>
          </>
        )}
      </ClipWrapper>
    </Flex>
  );
};

const ClipSelect = ({
  mixASpecs,
  mixBSpecs,
  layer,
  section,
  value,
  onChange,
  onPlayClip,
  notesPlaying,
}: ClipSelectProps) => {
  const selectA = () => selectOption('A');
  const selectB = () => selectOption('B');

  const selectOption = value => {
    onChange({
      target: {
        name: `${section} ${layer}`,
        value,
      },
    });
  };

  return (
    <Box fontSize="11px" width="100%" backgroundColor="#121212" borderRadius="3px">
      {mixASpecs && (
        <Clip
          mixSpecs={mixASpecs}
          onClick={selectA}
          section={section}
          layer={layer}
          selected={value === 'A'}
          onPlayClip={() => onPlayClip('A')}
          notesPlaying={notesPlaying}
        />
      )}
      {mixBSpecs && (
        <Clip
          mixSpecs={mixBSpecs}
          onClick={selectB}
          section={section}
          layer={layer}
          selected={value === 'B'}
          onPlayClip={() => onPlayClip('B')}
          notesPlaying={notesPlaying}
        />
      )}
    </Box>
  );
};

export default ClipSelect;
